import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Search, CalendarClock, XCircle, ArrowLeft, Info, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtLongDate, eur } from "@/lib/format";
import { BrandLogo, StatusBadge, SectionLabel } from "@/components/Brand";
import { ScheduleStep } from "@/components/booking/ScheduleStep";

function Lookup({ onFound }) {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/manage/lookup", { code, contact });
      onFound(data, { code, contact });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5 rounded-3xl border bg-white p-6 sm:p-8 rise" data-testid="manage-lookup-form">
      <div className="space-y-2"><Label htmlFor="code">Codice prenotazione</Label>
        <Input id="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="AS-XXXXXX" className="font-mono" required data-testid="manage-code-input" /></div>
      <div className="space-y-2"><Label htmlFor="contact">Email o telefono usati nella prenotazione</Label>
        <Input id="contact" value={contact} onChange={(e) => setContact(e.target.value)} required data-testid="manage-contact-input" /></div>
      <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="manage-lookup-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Trova prenotazione
      </Button>
    </form>
  );
}

function Reschedule({ booking, creds, onDone, onBack }) {
  const [form, setForm] = useState({ kind: booking.kind, equipment_type: booking.equipment_type || "", lat: booking.lat, lng: booking.lng, date: "", start: "", end: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/manage/reschedule", { ...creds, date: form.date, start: form.start, end: form.end });
      toast.success("Nuova data inviata: ti confermeremo su WhatsApp");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5 rounded-3xl border bg-white p-6" data-testid="manage-reschedule">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="manage-reschedule-back"><ArrowLeft className="h-4 w-4" /> Indietro</button>
      <h2 className="font-display text-xl font-semibold">Scegli una nuova data</h2>
      <ScheduleStep form={form} set={setForm} jobCode={booking.code} />
      <Button onClick={save} disabled={busy || !form.date || !form.start} className="w-full rounded-full" data-testid="manage-reschedule-confirm">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Conferma nuova data
      </Button>
    </div>
  );
}

function Proposal({ p, onAccept }) {
  const [busy, setBusy] = useState(false);
  const accept = async () => { setBusy(true); await onAccept(); setBusy(false); };
  return (
    <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="manage-proposal">
      <p>Ti abbiamo proposto una nuova data: <b data-testid="manage-proposal-when">{fmtLongDate(p.date)} · {p.start}–{p.end}</b></p>
      {p.acceptable ? (
        <>
          <Button onClick={accept} disabled={busy} className="w-full rounded-full bg-[#61a229] hover:bg-[#528a22]" data-testid="manage-accept-proposal-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Accetta data proposta
          </Button>
          <p className="text-xs text-amber-800">Non ti va bene? Scegli un'altra data qui sotto.</p>
        </>
      ) : <p className="text-xs" data-testid="manage-proposal-expired">La proposta non può più essere accettata online: contattaci su WhatsApp.</p>}
    </div>
  );
}

function Details({ b, onReschedule, onCancel, onAccept }) {
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);
  return (
    <div className="space-y-5 rounded-3xl border bg-white p-6 sm:p-8 rise" data-testid="manage-details">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>{b.code}</SectionLabel><StatusBadge status={b.status} testid="manage-status" />
      </div>
      <div>
        <h2 className="font-display text-2xl font-bold">{b.kind_label}{b.equipment_label ? ` · ${b.equipment_label}` : ""}</h2>
        <p className="mt-1 text-slate-600" data-testid="manage-when">{fmtLongDate(b.date)} · {b.start}–{b.end}</p>
        <p className="text-sm text-slate-500">{b.client_name} · {b.address}</p>
        {b.call_fee != null && <p className="text-sm text-slate-500" data-testid="manage-call-fee">Diritto di chiamata: {eur(b.call_fee)} + IVA · il resto dipende da tempo e ricambi</p>}
      </div>
      {b.proposal && <Proposal p={b.proposal} onAccept={onAccept} />}
      {b.editable ? (
        asking ? (
          <div className="space-y-3 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="manage-cancel-box">
            <Label>Motivo (facoltativo)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="manage-cancel-reason" />
            <div className="flex gap-2">
              <Button variant="destructive" className="rounded-full" onClick={() => onCancel(reason)} data-testid="manage-cancel-confirm">Conferma annullamento</Button>
              <Button variant="ghost" onClick={() => setAsking(false)} data-testid="manage-cancel-abort">Non annullare</Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <Button onClick={onReschedule} className="rounded-full" data-testid="manage-reschedule-button"><CalendarClock className="h-4 w-4" /> Cambia data o ora</Button>
            <Button variant="outline" onClick={() => setAsking(true)} className="rounded-full text-red-600" data-testid="manage-cancel-button"><XCircle className="h-4 w-4" /> Annulla prenotazione</Button>
          </div>
        )
      ) : (
        <p className="flex gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="manage-not-editable">
          <Info className="h-4 w-4 shrink-0" />
          {["in_attesa", "confermato"].includes(b.status)
            ? `Modifiche e annullamenti online sono possibili fino a ${b.min_notice_hours} ore prima dell'appuntamento. Per urgenze contattaci su WhatsApp.`
            : "Questa prenotazione non è più modificabile."}
        </p>
      )}
    </div>
  );
}

export default function ManageBookingPage() {
  const [booking, setBooking] = useState(null);
  const [creds, setCreds] = useState(null);
  const [mode, setMode] = useState("view");
  const cancel = async (reason) => {
    try {
      setBooking((await api.post("/public/manage/cancel", { ...creds, reason: reason || null })).data);
      toast.success("Prenotazione annullata");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const accept = async () => {
    try {
      setBooking((await api.post("/public/manage/accept-proposal", creds)).data);
      toast.success("Data accettata: ti confermeremo a breve l'intervento");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="min-h-screen">
      <header className="border-b bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3.5">
          <BrandLogo />
          <Link to="/" className="text-sm font-medium text-slate-600 hover:text-[#61a229]" data-testid="manage-new-booking-link">Nuova prenotazione</Link>
        </div>
      </header>
      <main className="mx-auto max-w-2xl space-y-6 px-4 py-10">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#61a229]">Area clienti</p>
          <h1 className="mt-2 font-display text-4xl font-bold">Gestisci la tua prenotazione</h1>
        </div>
        {!booking ? <Lookup onFound={(b, c) => { setBooking(b); setCreds(c); }} />
          : mode === "reschedule" ? <Reschedule booking={booking} creds={creds} onBack={() => setMode("view")} onDone={(b) => { setBooking(b); setMode("view"); }} />
          : <Details b={booking} onReschedule={() => setMode("reschedule")} onCancel={cancel} onAccept={accept} />}
      </main>
    </div>
  );
}
